import React, { useCallback, useEffect, useState } from 'react';
import { Building2, Home, Loader2, Pencil, Repeat, X } from 'lucide-react';
import { Input } from '@/components/ui';
import { useOnboarding, useStepSaver, useStepValidity } from '../../../contexts/OnboardingContext';
import { useAppConfig } from '../../../contexts/AppConfigContext';
import { useTenantData } from '../../../contexts/TenantDataContext';
import type { Routemize_DTOs_CompanyLocationDto } from '../../../api/models/Routemize_DTOs_CompanyLocationDto';
import type { UserAddress } from '../../../business/users/AddressManager';
import { ONBOARDING_STEP } from '../../../business/onboarding/OnboardingManager';
import {
  addressFieldsFrom,
  emptyAddressFields,
  formatAddressLine,
  isAddressComplete,
  loadWorkAreaContext,
  resolveEndLocation,
  saveBaseLocations,
  saveOfficeLocation,
  type AddressFieldValues,
  type BaseLocationChoice,
  type EndLocationChoice,
} from '../../../business/onboarding/onboardingLocations';
import { AddressFields } from '../shared/AddressFields';
import { OptionCard } from '../shared/OptionCard';
import { FieldLabel, Section, StepIntro } from '../shared/StepShell';

export const BaseLocationStep: React.FC = () => {
  const { completeStep } = useOnboarding();
  const { config } = useAppConfig();
  const { country: tenantCountry } = useTenantData();
  const userId = config?.currentUser?.id;

  const [isLoading, setIsLoading] = useState(true);
  const [startLocation, setStartLocation] = useState<BaseLocationChoice>('home');
  const [endLocation, setEndLocation] = useState<EndLocationChoice>('same');

  const [homeAddress, setHomeAddress] = useState<UserAddress | null>(null);
  const [officeLocation, setOfficeLocation] = useState<Routemize_DTOs_CompanyLocationDto | null>(
    null
  );
  const [officeFields, setOfficeFields] = useState<AddressFieldValues>(() =>
    emptyAddressFields(tenantCountry)
  );
  const [officeName, setOfficeName] = useState('Main Office');
  const [editingOffice, setEditingOffice] = useState(false);

  useEffect(() => {
    let cancelled = false;

    loadWorkAreaContext(userId)
      .then(({ homeAddress: home, officeLocation: office, settings }) => {
        if (cancelled) return;

        setHomeAddress(home);
        setOfficeLocation(office);

        if (office) {
          setOfficeName(office.locationName ?? 'Main Office');
          setOfficeFields(addressFieldsFrom(office, tenantCountry));
        }

        const prefs = settings?.routePreferences;
        const savedStart =
          prefs?.defaultStartingLocation === 'office' ? 'office' : ('home' as BaseLocationChoice);
        setStartLocation(savedStart);

        // "Same as start" isn't stored separately — the backend holds a
        // concrete location. Re-derive the choice so the UI matches the data
        // instead of relying on a browser-local copy that can go stale.
        const savedEnd = prefs?.defaultEndingLocation;
        if (!savedEnd || savedEnd === savedStart) {
          setEndLocation('same');
        } else {
          setEndLocation(savedEnd === 'office' ? 'office' : 'home');
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userId, tenantCountry]);

  const resolvedEnd = resolveEndLocation(endLocation, startLocation);
  const usesOffice = startLocation === 'office' || resolvedEnd === 'office';
  const needsOfficeAddress = usesOffice && !officeLocation;
  const officeFormOpen = needsOfficeAddress || editingOffice;

  useStepValidity(!officeFormOpen || isAddressComplete(officeFields));

  useStepSaver(
    ONBOARDING_STEP.BASE,
    useCallback(async () => {
      if (officeFormOpen) {
        setOfficeLocation(await saveOfficeLocation(officeName, officeFields, officeLocation));
        setEditingOffice(false);
      }
      await saveBaseLocations({ startLocation, endLocation });
      completeStep(ONBOARDING_STEP.BASE);
    }, [
      officeFormOpen,
      officeName,
      officeFields,
      officeLocation,
      startLocation,
      endLocation,
      completeStep,
    ])
  );

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-accent-600" />
      </div>
    );
  }

  const homeSubtitle = homeAddress
    ? formatAddressLine(homeAddress) || 'Your home address'
    : 'Set on the previous step';
  const officeSubtitle = officeLocation
    ? formatAddressLine(officeLocation) || officeLocation.locationName || 'Your office'
    : "We'll ask for the address below";

  return (
    <div className="space-y-4">
      <StepIntro>
        Routes are built out from these points, so the first and last jobs of the day are the
        closest ones to you.
      </StepIntro>

      <Section title="Your day starts at" required>
        <div className="grid gap-2.5 sm:grid-cols-2" role="radiogroup" aria-label="Start location">
          <OptionCard
            selected={startLocation === 'home'}
            onSelect={() => setStartLocation('home')}
            icon={Home}
            title="Home"
            badge="Common"
            description={homeSubtitle}
          />
          <OptionCard
            selected={startLocation === 'office'}
            onSelect={() => setStartLocation('office')}
            icon={Building2}
            title="Office"
            description={officeSubtitle}
          />
        </div>
      </Section>

      <Section title="Your day ends at" required>
        <div className="grid gap-2.5 sm:grid-cols-3" role="radiogroup" aria-label="End location">
          <OptionCard
            selected={endLocation === 'same'}
            onSelect={() => setEndLocation('same')}
            icon={Repeat}
            title="Same as start"
            badge="Common"
            description={startLocation === 'home' ? 'Back home' : 'Back to the office'}
          />
          <OptionCard
            selected={endLocation === 'home'}
            onSelect={() => setEndLocation('home')}
            icon={Home}
            title="Home"
          />
          <OptionCard
            selected={endLocation === 'office'}
            onSelect={() => setEndLocation('office')}
            icon={Building2}
            title="Office"
          />
        </div>
      </Section>

      {usesOffice && (
        <Section
          title="Office address"
          description={
            needsOfficeAddress
              ? 'Needed because your day starts or ends at the office.'
              : 'Where routes start or end.'
          }
          required={needsOfficeAddress}
          action={
            officeLocation ? (
              // eslint-disable-next-line no-restricted-syntax -- inline text action inside a section header; Button adds too much weight
              <button
                type="button"
                onClick={() => {
                  if (editingOffice) {
                    setEditingOffice(false);
                    setOfficeFields(addressFieldsFrom(officeLocation, tenantCountry));
                    setOfficeName(officeLocation.locationName ?? 'Main Office');
                  } else {
                    setEditingOffice(true);
                  }
                }}
                className="flex items-center gap-1 rounded-md px-1.5 py-1 text-xs font-medium text-accent-600 transition-colors hover:bg-accent-50"
              >
                {editingOffice ? <X className="h-3 w-3" /> : <Pencil className="h-3 w-3" />}
                {editingOffice ? 'Cancel' : 'Change'}
              </button>
            ) : undefined
          }
        >
          {officeFormOpen ? (
            <>
              <div className="mb-3">
                <FieldLabel htmlFor="onboarding-office-name">Location name</FieldLabel>
                <Input
                  id="onboarding-office-name"
                  value={officeName}
                  onChange={e => setOfficeName(e.target.value)}
                  placeholder="Main Office"
                />
              </div>
              <AddressFields
                value={officeFields}
                onChange={setOfficeFields}
                placeholder="Start typing your office address…"
              />
            </>
          ) : (
            <div className="flex items-start gap-2.5 rounded-lg border border-ink-100 bg-ink-50 p-3">
              <Building2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-ink-400" />
              <div className="min-w-0">
                {officeLocation?.locationName && (
                  <p className="text-2xs font-semibold uppercase tracking-wide text-ink-500">
                    {officeLocation.locationName}
                  </p>
                )}
                <p className="truncate text-sm font-medium text-ink-900">
                  {officeLocation?.addressLine1 || 'Address on file'}
                </p>
                <p className="text-xs text-ink-500">{formatAddressLine(officeLocation ?? {})}</p>
              </div>
            </div>
          )}
        </Section>
      )}
    </div>
  );
};
